import React, { useState, useContext, useRef, useEffect } from 'react';

import { useQuery } from '@apollo/client';

import { GET_COMMITMENTS_FOR_COMMITMENT_LIST } from 'api/queries';
import { getDropTrailerValue, getLaneRequirements } from 'helpers/ccHelpers';

import { MediaQueryContext } from '@arrive/responsive-provider';
import { ErrorContext } from 'components/ErrorWrapper/ErrorWrapper';

import { InfoCell, InfoSection, SectionTitle } from "components/CPCommittedCapacity/components/CommonComponents";
import RateconLoadTenderSection from 'components/CPCommittedCapacity/components/RateconLoadTenderSection/RateconLoadTenderSection';
import EmptyCommitmentsState from 'components/CPCommittedCapacity/components/EmptyCommitmentsState/EmptyCommitmentsState';
import CommitmentsHeader from 'components/CPCommittedCapacity/components/CommitmentsHeader/CommitmentsHeader';
import { MilestonesCell } from 'components/CPCommittedCapacity/components/MilestonesCell/MilesstonesCell';
import MetricsSection from 'components/CPCommittedCapacity/components/MetricsSection/MetricsSection';
import LaneWidget, {
  FULL_SORT_OPTIONS,
  LaneData
} from 'components/CPCommittedCapacity/components/LaneWidget/LaneWidget';
import { CommittedCapacityContactRepSection } from 'components/ContactRepSection/ContactRepSection';

import { EQ_TYPE_ID_MAP } from 'constants/equipmentConstants';
import { SortByType, SortDirectionEnum } from 'sharedTypes/common/sortByType';
import { GENERIC_ERROR } from 'constants/appConstants';
import {
  COMMITTED_CAPACITY_PENDO_LANES_LIST
} from 'constants/cPCommittedCapacityConstants';

import Tooltip from "@arrive/tooltip";
import { CompactPriceFormatter, PricingFormatter } from 'helpers/formatters';
import SVGIcon from "@arrive/svg-icon";

import INFO from '@arrive/ui-icons/svg/ic_info.svg';
import LEFT_ARROW from '@arrive/ui-icons/svg/ic_left_arrow.svg';

import styles from './CommitmentsTab.scss';

const SHOW_COMMITMENT_DAY_THRESHOLD = 90;

enum CC_LANE_TYPE {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}


export const TooltipTitle = (title: string, tooltipContent:string) => (
  <>
    {title}
    <Tooltip
      popperContent={tooltipContent}
      placement={'top'}
      classes={{ anchorClass: styles.infoIcon }}
    >
      <SVGIcon svgRef={INFO} />
    </Tooltip>
  </>
)

const CommitmentsTab = () => {
  const currentMediaTypes = useContext(MediaQueryContext);
  const [selectedLaneType, setSelectedLaneType] = useState(CC_LANE_TYPE.ACTIVE);
  const [selectedSort, setSelectedSort] = useState<SortByType>({
    ColumnToSort: 'createdOn',
    SortDirection: SortDirectionEnum.ASC
  });

  const { addError, errors, clearErrors } = useContext(ErrorContext);
  const isSmallOrXs = currentMediaTypes.xs || currentMediaTypes.sm;

  const { data = { getCommitmentsById: [] as LaneData[] }, loading, error, refetch } = useQuery(GET_COMMITMENTS_FOR_COMMITMENT_LIST, {
    variables: {
      commitmentFilter: {
        isEnabled: selectedLaneType === CC_LANE_TYPE.ACTIVE
      },
      sortBy: selectedSort
    }
  });

  const prevError = useRef(error);

  useEffect(() => {
    const isErrorUnlogged = error && !errors.some((loggedError: Error) => loggedError.message.includes(GENERIC_ERROR));

    if (!loading && error && isErrorUnlogged && addError) {
      addError(new Error(GENERIC_ERROR), refetch, error);
    }
  }, [addError, error, errors, loading, refetch]);

  useEffect(() => {
    if (error !== prevError.current) {
      prevError.current = error;

      if (!error) {
        clearErrors();
      }
    }
  }, [clearErrors, error]);

  const [commitment, setCommitment] = useState<LaneData | null>(null);

  useEffect(() => {
    if (!loading && !isSmallOrXs) {
      setCommitment(data.getCommitmentsById[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const trackSetLaneType = (newLaneType: CC_LANE_TYPE) => {
    if (window.pendo) {
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_LANES_LIST} Lane Type Filter`, {
        LaneType: newLaneType
      });
    }

    setSelectedLaneType(newLaneType);
  };

  const trackSetSortType = (newSort: SortByType) => {
    if (window.pendo) {
      const sortId = `${newSort.ColumnToSort}-${newSort.SortDirection}`;
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_LANES_LIST} Lane Sort`, {
        Sort: FULL_SORT_OPTIONS[sortId]?.content
      });
    }

    setSelectedSort(newSort);
  };

  const trackSetCommitment = (newCommitment: LaneData) => {
    if (window.pendo) {
      const sortId = `${selectedSort.ColumnToSort}-${selectedSort.SortDirection}`;
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_LANES_LIST} Select Commitment`, {
        LaneType: selectedLaneType,
        Sort: FULL_SORT_OPTIONS[sortId]?.content,
        CommitmentObject: newCommitment
      });
    }

    setCommitment(newCommitment);
  };

  //Days Remaining field logic
  const commitmentDaysRemaining = commitment?.commitmentDaysRemaining;
  const commitmentDaysTitle = commitmentDaysRemaining > SHOW_COMMITMENT_DAY_THRESHOLD ? "Commitment Status" : "Commitment Days Remaining";
  const commitmentDaysTooltipTitle = commitmentDaysRemaining > SHOW_COMMITMENT_DAY_THRESHOLD ? commitmentDaysTitle : TooltipTitle(commitmentDaysTitle, 'The days remaining until commitment expiry');

  const showInactiveLaneType = selectedLaneType === 'INACTIVE' ? ' (Inactive)' : '';
  const numberOfDays = (commitmentDaysRemaining > 0) ? `${commitmentDaysRemaining} Days` : `0 Days${showInactiveLaneType}`;
  const commitmentDaysContent = commitmentDaysRemaining > SHOW_COMMITMENT_DAY_THRESHOLD ? 'Ongoing' : numberOfDays;

  return (
    <div data-testid='commitments-tab-content' className={styles.commitmentContainer}>
      {((!isSmallOrXs) || (isSmallOrXs && !commitment)) &&
      <div className={styles.leftColumn}>
        <LaneWidget
          data={data.getCommitmentsById}
          commitment={commitment}
          setCommitment={trackSetCommitment}
          setSelectedLaneType={trackSetLaneType}
          selectedLaneType={selectedLaneType}
          setSelectedSort={trackSetSortType}
          selectedSort={selectedSort}
          loading={loading} />
        {!loading && <div className={styles.contactRepSection}>
          <CommittedCapacityContactRepSection origin={commitment?.origin} destination={commitment?.destination} rate={commitment?.rate} />
        </div>}
      </div>}
      {((!isSmallOrXs) || (isSmallOrXs && commitment)) && <div className={styles.rightColumn}>
        {!loading && !commitment ? <EmptyCommitmentsState activeLanesSelected={selectedLaneType === 'ACTIVE'}/> :
          <>
            <div data-testid={'commitment-overview-section'}>
              {isSmallOrXs && <div className={styles.mobileBackButtonContainer} onClick={() => setCommitment(null)}>
                <SVGIcon className={styles.mobileBackButton} svgRef={LEFT_ARROW}/> Back
              </div>}
              <CommitmentsHeader commitment={commitment} loading={loading} />
              <SectionTitle title={'Milestones'} />
              <InfoSection>
                <MilestonesCell
                  title={"Total Loads Moved on this lane"}
                  content={commitment?.totalLoads}
                  testIdString={'Total Loads Moved'}
                  loading={loading}
                />
                <MilestonesCell
                  title={commitmentDaysTitle}
                  content={commitmentDaysContent}
                  testIdString={`${commitmentDaysTitle} Milestone`}
                  loading={loading}
                />
                <MilestonesCell
                  title={"Your Estimated Revenue on this lane"}
                  content={PricingFormatter.format(commitment?.revenueAddedFsc)}
                  testIdString={'Revenue on lane'}
                  loading={loading}
                />
                <MilestonesCell
                  title={"Miles Driven"}
                  content={commitment?.totalMiles}
                  testIdString={'Miles Driven'}
                  loading={loading}
                />
              </InfoSection>
            </div>
            <MetricsSection laneCarrierId={commitment?.carrierId} loadingCommitments={loading} isXS={currentMediaTypes.xs} />
            <SectionTitle title={'Commitment Preferences'} />
            <InfoSection>
              <InfoCell
                title={commitmentDaysTooltipTitle}
                content={commitmentDaysContent}
                testIdString={commitmentDaysTitle}
                loading={loading}
              />
              <InfoCell
                title={'Pickup Days'}
                content={commitment?.pickupDays}
                testIdString={'Pickup Days'}
                loading={loading}
              />
              <InfoCell
                title={TooltipTitle("Lead Time Prior To Pickup", 'The minimum lead time needed, represented in hours to trigger an actionable tender via email')}
                content={`${commitment?.leadTime} Hours`}
                testIdString={'Lead Time Prior To Pickup'}
                loading={loading}
              />
              <InfoCell
                title={TooltipTitle("Coverage Allowance", 'The maximum number of loads to be tendered per day and per week')}
                content={`${commitment?.loadsPerDay} per day / ${commitment?.loadsPerWeek} per week`}
                testIdString={'Coverage Allowance'}
                loading={loading}
              />
              <InfoCell
                title={'Delivery Days'}
                content={commitment?.deliveryDays}
                testIdString={'Delivery Days'}
                loading={loading}
              />
            </InfoSection>
            <SectionTitle title={'Load Details'} />
            <InfoSection>
              <InfoCell
                title={'Equipment'}
                content={EQ_TYPE_ID_MAP[commitment?.truckEquipmentTypeId]}
                testIdString={'Equipment'}
                loading={loading}
              />
              <InfoCell
                title={'Cargo Value'}
                content={CompactPriceFormatter.format(commitment?.maximumValueAccepted)}
                testIdString={'Cargo Value'}
                loading={loading}
              />
              <InfoCell
                title={'Drop Trailer'}
                content={getDropTrailerValue(commitment)}
                testIdString={'Drop Trailer'}
                loading={loading}
              />
              <InfoCell
                title={'Special Conditions'}
                content={getLaneRequirements(commitment)}
                testIdString={'Special Conditions'}
                loading={loading}
              />
            </InfoSection>
            <RateconLoadTenderSection carrierId={commitment?.carrierId} rateConEmail={commitment?.rateConEmail} loadTenderEmail={commitment?.loadTenderEmail} loadingCommitment={loading} />
            <SectionTitle title={'Contact Rep'} />
            <InfoSection className={styles.noBorderBottom}>
              <InfoCell
                title={'Carrier Rep'}
                content={commitment?.carrierRepEmail}
                testIdString={'Carrier Rep'}
                className={styles.noCapitalize}
                loading={loading}
              />
            </InfoSection>
          </>}
      </div>}
    </div>
  );
};

export default CommitmentsTab;
